import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { useMyGoals } from '$tablet-app/src/app/goals/useGoals/useGoals';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { useCurrentProfile } from '$tablet-app/src/app/users';
import { Goal } from '@akin-home/goals';
import React, { useEffect, useMemo, useReducer } from 'react';
import GoalStatisticsScreenView from './GoalStatisticsScreenView';

interface State {
  tab: 'summary' | 'goals';
  activeGoal?: Goal;
  period: 'day' | 'week' | 'month';
}

type Action =
  | { type: 'CHANGE_TAB'; payload: { tab: 'summary' | 'goals' } }
  | { type: 'SELECT_GOAL'; payload: { goal: Goal } }
  | { type: 'CHANGE_PERIOD'; payload: { period: 'day' | 'week' | 'month' } };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'CHANGE_TAB':
      return {
        ...state,
        ...action.payload,
      };
    case 'SELECT_GOAL':
      return {
        ...state,
        activeGoal: action.payload.goal,
      };
    case 'CHANGE_PERIOD':
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

const GoalStatisticsScreen = (
  props: RootStackScreenProps<'GoalStatistics'>
) => {
  const {
    navigation: { goBack },
  } = props;

  const [state, dispatch] = useReducer(reducer, {
    tab: 'summary',
    period: 'day',
  });

  useUpdateScreenChatContext();

  const { data: goals } = useMyGoals();
  const { profile: user } = useCurrentProfile();

  const actions = useMemo(
    () => ({
      onChangeTab: (tab: 'summary' | 'goals') =>
        dispatch({ type: 'CHANGE_TAB', payload: { tab } }),
      onSelectGoal: (goal: Goal) =>
        dispatch({ type: 'SELECT_GOAL', payload: { goal } }),
      onChangePeriod: (period: 'day' | 'week' | 'month') =>
        dispatch({ type: 'CHANGE_PERIOD', payload: { period } }),
    }),
    [dispatch]
  );

  useEffect(() => {
    if (!state.activeGoal && goals.length > 0) {
      dispatch({ type: 'SELECT_GOAL', payload: { goal: goals[0] } });
    }
  }, [goals, state.activeGoal]);

  useEffect(() => {
    sendBackgroundMessage(intent_labels.open_goal_statistics_screen);
  }, []);

  return (
    <GoalStatisticsScreenView
      tab={state.tab}
      onPressBack={goBack}
      goals={goals}
      period={state.period}
      activeGoal={state.activeGoal}
      user_id={user.id}
      {...actions}
    />
  );
};

export default GoalStatisticsScreen;
